import { OrchestrationConnectionType } from '@common/Fabric/Connections/types';
import { isPreviewInstanceName } from '@common/sqlIde/BusinessApps/utils';
import { Entity, getAppEntityName } from '@common/types/Entity';
import { ClockIcon, FileDIcon, PlayIcon, SettingsBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { cronToReadableString } from '@prophecy/ui-v3/CronInput/utils';
import { Ellipsis } from '@prophecy/ui-v3/Ellipsis';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Text } from '@prophecy/ui-v3/Typography';
import { SqlPipelineRunType } from 'frontend/core/src/data/apis/types';
import { castArray } from 'frontend/core/src/data/util';
import { useMemo } from 'react';

import { OrchestrationSchedulePipelineInfo } from '../SQLPipeline/types';
import { StyledBadge } from './styled';

type TriggerProps = {
  cron: string;
  sensorConfigDetails: OrchestrationSchedulePipelineInfo['sensorConfigDetails'];
  runType: SqlPipelineRunType;
  appName?: string;
  instanceName?: string;
};

const runLabels = {
  [SqlPipelineRunType.Scheduled]: { label: 'Scheduled Run', icon: <ClockIcon type='default' /> },
  [SqlPipelineRunType.InteractivePipelineRun]: { label: 'Pipeline Run', icon: <PlayIcon type='default' /> },
  [SqlPipelineRunType.InteractiveAppRun]: {
    label: `${getAppEntityName(Entity.App)} Run`,
    icon: <PlayIcon type='default' />
  },
  [SqlPipelineRunType.APIPipelineRun]: { label: 'API Run', icon: <SettingsBIcon type='default' /> },
  [SqlPipelineRunType.AppSchedule]: { label: 'Scheduled Run', icon: <ClockIcon type='default' /> }
};

type BadgeItem = {
  label: React.ReactNode;
  tooltip?: React.ReactNode;
  icon?: React.ReactNode;
};

const TriggerBagdes = ({ cron, sensorConfigDetails, runType, appName, instanceName }: TriggerProps) => {
  const items = useMemo(() => {
    const renderLabel = (value: string) => (
      <Ellipsis tooltip>
        <Text level='sm' weight={theme.fontWeight.medium}>
          {value}
        </Text>
      </Ellipsis>
    );

    const getTriggerInfo = () => {
      if (runType === SqlPipelineRunType.InteractiveAppRun && appName) {
        const label = instanceName && !isPreviewInstanceName(instanceName) ? `${appName} - ${instanceName}` : appName;

        return {
          label: renderLabel(label),
          icon: <PlayIcon type='default' />,
          tooltip: (
            <Text level='sm'>
              {getAppEntityName(Entity.App)} Run of {label}
            </Text>
          )
        };
      }

      if (runType !== SqlPipelineRunType.Scheduled && runType !== SqlPipelineRunType.AppSchedule) {
        return {
          ...runLabels[runType],
          label: renderLabel(runLabels[runType].label)
        };
      }

      if (sensorConfigDetails.length > 0) {
        return sensorConfigDetails
          .sort((a, b) => a.sequenceId - b.sequenceId)
          .map(({ configName, properties, connector, sequenceId }) => {
            const filePathKey = connector.kind === OrchestrationConnectionType.S3 ? 's3Path' : 'filePath';

            return {
              label: renderLabel(`${connector.kind.toUpperCase()} Trigger ${sequenceId}`),
              tooltip: (
                <Stack>
                  <Stack direction='horizontal' gap={theme.spaces.x24} align='space-between'>
                    <Text level='xs' tone={theme.colors.gray400}>
                      Config name
                    </Text>
                    <Text level='xs'>{configName}</Text>
                  </Stack>
                  <Stack direction='horizontal' gap={theme.spaces.x24} align='space-between'>
                    <Text level='xs' tone={theme.colors.gray400}>
                      Connection
                    </Text>
                    <Text level='xs'>{connector.properties.id}</Text>
                  </Stack>
                  <Stack direction='horizontal' gap={theme.spaces.x24} align='space-between'>
                    <Text level='xs' tone={theme.colors.gray400}>
                      File Path
                    </Text>
                    <Text level='xs'>{properties[filePathKey]}</Text>
                  </Stack>
                </Stack>
              ),
              icon: <FileDIcon type='default' />
            };
          });
      } else if (cron) {
        return { label: renderLabel(cronToReadableString(cron)), icon: <ClockIcon type='default' /> };
      }
      return {
        ...runLabels[SqlPipelineRunType.Scheduled],
        label: renderLabel(runLabels[SqlPipelineRunType.Scheduled].label)
      };
    };

    return castArray<BadgeItem>(getTriggerInfo());
  }, [cron, sensorConfigDetails, runType, appName, instanceName]);

  return (
    <Stack direction='horizontal' gap={theme.spaces.x6} padding={`${theme.spaces.x6} 0`} wrap>
      {items.map(({ label, tooltip, icon }, index) => (
        <StyledBadge
          key={index}
          iconPlacement='left'
          tooltip={tooltip}
          icon={icon}
          variant='badgeModern'
          size='m'
          tone='gray'>
          {label}
        </StyledBadge>
      ))}
    </Stack>
  );
};

export default TriggerBagdes;
